import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  readGuestAddress,
  writeGuestAddress,
  clearGuestAddress,
  type GuestAddress,
} from '@/lib/guestAddress';
import {
  accountApi,
  type Address as AccountAddress,
  type PropertyType,
} from '@/lib/api/account';
import { useAuth } from './AuthContext';

/**
 * AddressContext — single source of truth for the customer's currently
 * active service location.
 *
 * Resolution order:
 *   1. Authenticated customer with a default saved address  → that one
 *   2. Authenticated customer with at least one saved address → first
 *   3. Guest with a localStorage address                    → that one
 *   4. Nothing                                              → null
 *
 * AddressGate (in components/shared) reads `status` and `address` to
 * decide whether to render its children or redirect to /address.
 *
 * `setAddress(...)` updates both the context state and the appropriate
 * persistence layer (localStorage for guests, backend for authed users).
 */

export interface ActiveAddress {
  source: 'guest' | 'account';
  // Stable id for authed addresses; synthetic 'guest' marker otherwise.
  id: string;
  label: string;
  lat: number;
  lng: number;
  city: string | null;
  district: string | null;
  street: string | null;
  building: string | null;
  postalCode: string | null;
  formatted: string | null;
  // Property type only flows through for account-sourced addresses.
  // Guest pins don't capture a type — the customer picks one in the
  // booking wizard, which then PATCHes it back to the saved address
  // so the next visit pre-fills it.
  type: PropertyType | null;
  zone: { id: string; nameEn: string; nameAr: string } | null;
  resolvedDistrict: { id: string; nameEn: string; nameAr: string } | null;
}

type Status = 'loading' | 'resolved';

interface AddressContextValue {
  status: Status;
  address: ActiveAddress | null;
  /** Persist the picked address and update context. */
  setGuestAddress: (g: GuestAddress) => void;
  /** Mark a server-side address as the currently active one (used after
   *  the authed customer creates or selects an address). */
  setAccountAddress: (a: AccountAddress) => void;
  /** Re-fetch saved addresses from the backend and re-resolve the active
   *  pick. Call after a createAddress / updateAddress / deleteAddress so
   *  the context picks up the change without a full reload. */
  refresh: () => Promise<void>;
  /** Drop the active address and force re-pick on next gate check. */
  clearAddress: () => void;
}

const AddressContext = createContext<AddressContextValue | null>(null);

function guestToActive(g: GuestAddress): ActiveAddress {
  return {
    source: 'guest',
    id: 'guest',
    label: g.label,
    lat: g.lat,
    lng: g.lng,
    city: g.city,
    district: g.district,
    street: g.street,
    building: g.building,
    postalCode: g.postalCode,
    formatted: g.formatted,
    type: null,
    zone: g.zone,
    resolvedDistrict: g.resolvedDistrict,
  };
}

function accountToActive(a: AccountAddress): ActiveAddress {
  return {
    source: 'account',
    id: a.id,
    label: a.label,
    // Authed addresses may not have lat/lng if they were created via
    // the legacy admin UI. Default to NaN so callers detect "incomplete"
    // and prompt the customer to confirm on the map.
    lat: a.lat ?? Number.NaN,
    lng: a.lng ?? Number.NaN,
    city: a.city,
    district: a.district,
    street: a.street,
    building: a.building ?? null,
    postalCode: a.postalCode ?? null,
    formatted: null,
    type: a.type,
    // The legacy `zone` field is a free-text string on AccountAddress;
    // it does NOT match the serviceability zone shape. Leave null and
    // let the home page resolve via servicesByArea() when needed.
    zone: null,
    resolvedDistrict: null,
  };
}

export function AddressProvider({ children }: { children: ReactNode }) {
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [address, setAddress] = useState<ActiveAddress | null>(null);
  // Bumping this triggers a re-run of the resolve effect — used by the
  // public `refresh()` API after a customer creates/updates an address.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Resolve the active address whenever auth state (or refresh nonce) changes.
  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      setStatus('loading');

      if (auth.status === 'loading') return;

      if (auth.status === 'authenticated') {
        let list: AccountAddress[] = [];
        let listFailed = false;
        try {
          list = await accountApi.listAddresses();
        } catch {
          // listAddresses hiccup — don't null the context out. Fall
          // through and try the guest pin so the customer doesn't
          // lose what they pinned right before signing in.
          listFailed = true;
        }
        if (cancelled) return;

        if (!listFailed && list.length > 0) {
          const def = list.find((a) => a.isDefault) ?? list[0];
          setAddress(accountToActive(def));
          // Authed customer has a server-side address — discard any
          // leftover guest pin so the two don't diverge.
          clearGuestAddress();
          setStatus('resolved');
          return;
        }

        // Either zero saved addresses or a transient listAddresses
        // failure. Either way, the guest pin (if any) is the right
        // thing to surface so the customer doesn't lose what they
        // pinned before signing in. Backend validation requires
        // non-empty `street` (@MinLength(1)) — Nominatim doesn't
        // always return one, so fall back to a coord-based placeholder
        // so the migration never silently fails on valid pins.
        const guest = readGuestAddress();
        if (guest && Number.isFinite(guest.lat) && Number.isFinite(guest.lng)) {
          if (listFailed) {
            // Transient list failure — don't risk creating a duplicate
            // when we don't actually know whether account addresses
            // exist. Show the guest pin; the next refresh will retry
            // the list + migration.
            setAddress(guestToActive(guest));
            setStatus('resolved');
            return;
          }
          const coordLabel = `${guest.lat.toFixed(5)}, ${guest.lng.toFixed(5)}`;
          const streetFallback =
            (guest.street && guest.street.trim()) ||
            (guest.district && guest.district.trim()) ||
            (guest.formatted && guest.formatted.split(',')[0]?.trim()) ||
            `Pinned location (${coordLabel})`;
          try {
            const created = await accountApi.createAddress({
              label: guest.label || 'Home',
              type: 'apartment',
              city:
                (guest.city && guest.city.trim()) ||
                (guest.zone?.nameEn && guest.zone.nameEn.trim()) ||
                '—',
              district:
                (guest.district && guest.district.trim()) ||
                (guest.resolvedDistrict?.nameEn && guest.resolvedDistrict.nameEn.trim()) ||
                '—',
              street: streetFallback,
              building: guest.building?.trim() || undefined,
              postalCode: guest.postalCode?.trim() || undefined,
              zone:
                (guest.zone?.nameEn && guest.zone.nameEn.trim()) ||
                (guest.city && guest.city.trim()) ||
                'Unzoned',
              lat: guest.lat,
              lng: guest.lng,
              isDefault: true,
            });
            if (cancelled) return;
            clearGuestAddress();
            setAddress(accountToActive(created));
            // Tell any saved-address queries (AddressesPage, wizard
            // Step "where") that the list just gained a row.
            queryClient.invalidateQueries({ queryKey: ['addresses'] });
            setStatus('resolved');
            return;
          } catch {
            // Migration failed (validation, network, etc.). KEEP the
            // guest pin so the customer doesn't lose what they placed
            // — surface it as the active address; the next refresh
            // will retry the migration.
            setAddress(guestToActive(guest));
            setStatus('resolved');
            return;
          }
        } else if (guest) {
          // Guest pin without valid coords — useless for booking, drop it.
          clearGuestAddress();
        }

        if (cancelled) return;
        setAddress(null);
        setStatus('resolved');
        return;
      }

      // Guest mode — read whatever pin (if any) is in localStorage.
      const guest = readGuestAddress();
      if (cancelled) return;
      setAddress(guest ? guestToActive(guest) : null);
      setStatus('resolved');
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [auth.status, refreshNonce]);

  const setGuestAddress = useCallback((g: GuestAddress) => {
    writeGuestAddress(g);
    setAddress(guestToActive(g));
    setStatus('resolved');
  }, []);

  const setAccountAddress = useCallback(
    (a: AccountAddress) => {
      // Authed customer just created or selected a server-side address —
      // promote it to active immediately so the next render sees it
      // without waiting for a listAddresses round-trip.
      clearGuestAddress();
      setAddress(accountToActive(a));
      // Invalidate cached saved-address queries so consumer pages
      // (AddressesPage, wizard Step "where") see the new row.
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      setStatus('resolved');
    },
    [queryClient],
  );

  const refresh = useCallback(async () => {
    // Bump the nonce to re-run the resolve effect. Also invalidate the
    // saved-address query cache so consumer pages re-fetch in lockstep.
    queryClient.invalidateQueries({ queryKey: ['addresses'] });
    setRefreshNonce((n) => n + 1);
  }, [queryClient]);

  const clearAddress = useCallback(() => {
    clearGuestAddress();
    setAddress(null);
    setStatus('resolved');
  }, []);

  const value = useMemo<AddressContextValue>(
    () => ({
      status,
      address,
      setGuestAddress,
      setAccountAddress,
      refresh,
      clearAddress,
    }),
    [status, address, setGuestAddress, setAccountAddress, refresh, clearAddress],
  );

  return <AddressContext.Provider value={value}>{children}</AddressContext.Provider>;
}

export function useAddress() {
  const ctx = useContext(AddressContext);
  if (!ctx) throw new Error('useAddress must be used inside <AddressProvider>');
  return ctx;
}
